import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, TableSkeleton, StatusDot, CustomSelect,
  RowActions, RowAction, MoreMenu, ListPagination, ListSearchInput, ListEmptyRow, btnPrimary, btnSecondary,
} from '@redpanda/ui';
import { formatTableDate, isAdmin, toIntlLocale, useRefetchOnFocus, useRememberedState } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import {
  AttendanceRecord, LeaveRequest, useAttendanceStoreBase, useLeaveStoreBase, useAttendanceSummary, usePendingLeaveCount,
} from '../stores/attendanceStore';
import QuickCheckInDialog from '../components/QuickCheckInDialog';
import LeaveRequestDialog, { LEAVE_TYPES } from '../components/LeaveRequestDialog';
import AttendanceDialog from '../components/AttendanceDialog';
import { useListSearch } from '../hooks/useListSearch';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { pendingRowClass } from '../utils/pendingItem';
import { attendanceTone, leaveTone, toneOf } from '../utils/statusTone';
import { attendanceFlags, formatTime, thisMonthRange } from '../utils/attendance';
import { apiErrorMessage } from '../utils/apiError';
import { getAttendanceRecords, getLeaveRequests, reviewLeaveRequest } from '../services/api';

const TABS = ['attendance', 'leave'] as const;
type Tab = typeof TABS[number];
const LEAVE_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];

/**
 * 出勤：出勤記錄／請假兩個檢視（R24：搜尋在左、檢視切換在同一列靠右；目前檢視存在網址 ?tab=）
 * 本月摘要原本是漸層底＋emoji＋4 張彩色圖示卡，而且只用目前這一頁 20 筆計算 → 一行文字摘要，由後端算整個月
 */
const AttendanceListPage: React.FC = () => {
  const { t, i18n } = useTranslation(['pages', 'translation']);
  const locale = toIntlLocale(i18n.language);
  const { user } = useContext(UserContext)!;
  const admin = isAdmin(user?.role);
  // 檢視存在網址（?tab=）；網址沒指定時（從側欄進來）顯示上次看的檢視（同 PM 應收帳款頁）
  const [searchParams, setSearchParams] = useSearchParams();
  const [rememberedTab, rememberTab] = useRememberedState<Tab>('hr-ui:attendance.tab', 'attendance', TABS);
  const tabParam = searchParams.get('tab') as Tab | null;
  const tab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : rememberedTab;
  const setTab = (next: Tab) => {
    rememberTab(next);
    setSearchParams(next === 'leave' ? { tab: 'leave' } : {}, { replace: true });
  };
  useEffect(() => { if (tabParam && TABS.includes(tabParam)) rememberTab(tabParam); }, [tabParam, rememberTab]); // 從連結帶 ?tab= 進來也算最後看的檢視

  const attendanceStore = useAttendanceStoreBase();
  const leaveStore = useLeaveStoreBase();

  const [searchTerm, setSearchTerm] = useState('');
  const [leaveStatus, setLeaveStatus] = useState('');
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [attendanceDialog, setAttendanceDialog] = useState<AttendanceRecord | null | undefined>(undefined);
  const [leaveDialog, setLeaveDialog] = useState<LeaveRequest | null | undefined>(undefined);

  // 本月摘要、待審核數：persist（先顯示上次的數字），進頁面與資料異動時重抓
  const [monthRange] = useState(thisMonthRange);
  const summary = useAttendanceSummary(monthRange);
  const pendingCount = usePendingLeaveCount('pending');
  useEffect(() => {
    attendanceStore.fetchItems(1);
    leaveStore.fetchItems(1);
    summary.refetch();
    pendingCount.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useDataChanged('attendance', summary.refetch);
  useDataChanged('leaves', pendingCount.refetch);
  // 離開 30 秒以上切回分頁時重抓（R33）
  useRefetchOnFocus(() => {
    useAttendanceStoreBase.getState().refreshData();
    useLeaveStoreBase.getState().refreshData();
    summary.refetch();
    pendingCount.refetch();
  });

  // 切換檢視時清掉搜尋與篩選
  useEffect(() => { setSearchTerm(''); setLeaveStatus(''); }, [tab]);

  // 搜尋／篩選由後端查（R29）
  const search = searchTerm.trim();
  const attendanceSearch = useListSearch<AttendanceRecord>(
    'attendance', tab === 'attendance' && search ? { search } : null, page => getAttendanceRecords(page, { search }),
  );
  const leaveQuery = tab === 'leave' && (search || leaveStatus) ? { search, status: leaveStatus } : null;
  const leaveSearch = useListSearch<LeaveRequest>('leaves', leaveQuery, page => getLeaveRequests(page, { search, status: leaveStatus }));

  const active = tab === 'attendance'
    ? { store: attendanceStore, result: attendanceSearch }
    : { store: leaveStore, result: leaveSearch };
  const listPagination = active.result.active ? active.result.pagination : active.store.pagination;
  const listLoading = active.result.active ? active.result.isLoading : active.store.isPageLoading;
  const showSkeleton = (active.store.isLoading && active.store.items.length === 0 && !active.result.active) || listLoading;
  const changePage = (page: number) => {
    if (active.result.active) return active.result.setPage(page);
    active.store.setCurrentPage(page);
    active.store.fetchItems(page);
  };
  const attendanceItems = attendanceSearch.active ? attendanceSearch.items : attendanceStore.items;
  const leaveItems = leaveSearch.active ? leaveSearch.items : leaveStore.items;

  // 審核：樂觀更新狀態，失敗回滾
  const review = async (request: LeaveRequest, status: 'APPROVED' | 'REJECTED') => {
    const store = useLeaveStoreBase.getState();
    store.updateItem(request.id, { status });
    try {
      await reviewLeaveRequest(request.id, status);
      toast.success(t(status === 'APPROVED' ? 'pages:attendance.leave.approve.success' : 'pages:attendance.leave.reject.success'));
      notifyDataChanged('leaves');
    } catch (error) {
      useLeaveStoreBase.getState().updateItem(request.id, { status: request.status });
      toast.error(apiErrorMessage(error, 'translation:common.messages.updateFailed'));
    }
  };

  const confirmDelete = async () => {
    await active.store.confirmDelete();
    notifyDataChanged(tab === 'attendance' ? 'attendance' : 'leaves');
  };

  const primaryButton = tab === 'attendance' ? (
    <button type="button" onClick={() => setAttendanceDialog(null)} className={btnPrimary}>{t('pages:attendance.list.addAttendance')}</button>
  ) : (
    <button type="button" onClick={() => setLeaveDialog(null)} className={btnPrimary}>{t('pages:attendance.list.addLeave')}</button>
  );

  const s = summary.data;
  const anomalies = s ? s.late + s.missingCheck : 0;

  const tabClass = (current: boolean) =>
    `inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm transition-colors ${
      current ? 'bg-accent-soft text-accent font-medium' : 'text-fg-secondary hover:text-fg'
    }`;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        {/* 手機寬度兩顆按鈕放不下時整組換到下一行（按鈕文字不折行） */}
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('pages:attendance.list.title')}</h1>
          <div className="flex space-x-3 whitespace-nowrap">
            <button type="button" onClick={() => setCheckInOpen(true)} className={btnSecondary}>{t('pages:attendance.list.quickCheckIn')}</button>
            {primaryButton}
          </div>
        </div>
      </div>

      {/* 搜尋（＋請假狀態篩選）在左、檢視切換在右 */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
          <ListSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('pages:attendance.list.searchPlaceholder')}
          />
          {tab === 'leave' && (
            <div className="md:w-40">
              <CustomSelect
                value={leaveStatus}
                onChange={setLeaveStatus}
                options={[
                  { value: '', label: t('pages:attendance.leave.allStatuses') },
                  ...LEAVE_STATUSES.map(value => ({ value, label: t(`translation:leaveStatus.${value}`) })),
                ]}
              />
            </div>
          )}
        </div>
        <nav aria-label={t('pages:attendance.list.viewLabel')} className="ml-auto inline-flex h-10 items-center rounded-md border border-line bg-surface p-0.5">
          <button type="button" aria-current={tab === 'attendance' ? 'page' : undefined} onClick={() => setTab('attendance')} className={tabClass(tab === 'attendance')}>
            {t('pages:attendance.list.tabs.records')}
          </button>
          <button type="button" aria-current={tab === 'leave' ? 'page' : undefined} onClick={() => setTab('leave')} className={tabClass(tab === 'leave')}>
            {t('pages:attendance.list.tabs.leaves')}
            {!!pendingCount.data && (
              <span className="min-w-5 rounded-full bg-amber-500/15 px-1.5 text-[13px] font-medium tabular-nums text-amber-700 dark:text-amber-300" title={t('pages:attendance.leave.pendingCount', { count: pendingCount.data })}>
                {pendingCount.data}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* 本月摘要：一行文字；只有遲到、缺打卡大於 0 才用紅字（旁邊有文字說明） */}
      {tab === 'attendance' && s && (
        <p className="mb-4 text-sm text-fg-secondary tabular-nums">
          {t('pages:attendance.list.summary.thisMonth')}・{t('pages:attendance.list.summary.records', { count: s.totalRecords })}
          ・{t('pages:attendance.list.summary.present', { count: s.present })}
          ・{t('pages:attendance.list.summary.leave', { count: s.leave })}
          ・<span className={s.late > 0 ? 'text-red-600 dark:text-red-400' : undefined}>{t('pages:attendance.list.summary.late', { count: s.late })}</span>
          ・<span className={s.missingCheck > 0 ? 'text-red-600 dark:text-red-400' : undefined}>{t('pages:attendance.list.summary.missingCheck', { count: s.missingCheck })}</span>
          {anomalies === 0 && s.totalRecords > 0 && <span>・{t('pages:attendance.list.summary.noAnomalies')}</span>}
        </p>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {showSkeleton ? (
        <TableSkeleton rows={10} columns={tab === 'attendance' ? 9 : 7} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {tab === 'attendance' ? (
              <Table style={{ tableLayout: 'fixed', minWidth: '960px' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead numeric className="w-24">{t('translation:common.table.columns.date')}</TableHead>
                    <TableHead className="w-32 !pl-8">{t('translation:common.table.columns.name')}</TableHead>
                    <TableHead className="w-28">{t('translation:common.table.columns.department')}</TableHead>
                    <TableHead numeric className="w-28 whitespace-nowrap">{t('translation:common.forms.labels.checkInTime')}</TableHead>
                    <TableHead numeric className="w-28 whitespace-nowrap">{t('translation:common.forms.labels.checkOutTime')}</TableHead>
                    <TableHead numeric className="w-20">{t('translation:common.table.columns.hours')}</TableHead>
                    <TableHead className="w-48 !pl-8">{t('translation:common.table.columns.status')}</TableHead>
                    <TableHead>{t('pages:attendance.form.notes')}</TableHead>
                    <TableHead className="w-32">{t('translation:common.table.columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendanceItems.length === 0 ? (
                    <ListEmptyRow
                      colSpan={9}
                      filtered={!!search}
                      title={t('pages:attendance.list.noAttendance')}
                      hint={t('pages:attendance.list.emptyHint')}
                      action={primaryButton}
                    />
                  ) : attendanceItems.map(record => {
                    const flags = attendanceFlags(record);
                    const flagLabels = [
                      flags.late && t('translation:attendanceStatus.LATE'),
                      flags.earlyLeave && t('translation:attendanceStatus.EARLY'),
                      (flags.missingCheckIn || flags.missingCheckOut) && t('pages:attendance.list.missingCheck'),
                    ].filter(Boolean);
                    return (
                      <TableRow key={record.id} className={pendingRowClass(record)}>
                        <TableCell numeric>{formatTableDate(record.date, i18n.language)}</TableCell>
                        <TableCell className="truncate font-medium text-fg !pl-8">{record.employee?.name || '—'}</TableCell>
                        <TableCell className="truncate">{record.employee?.department || <span className="text-fg-muted">—</span>}</TableCell>
                        <TableCell numeric className={flags.late ? '!text-red-600 dark:!text-red-400' : undefined}>
                          {formatTime(record.checkInTime, locale) || <span className="text-fg-muted">—</span>}
                        </TableCell>
                        <TableCell numeric className={flags.earlyLeave ? '!text-red-600 dark:!text-red-400' : undefined}>
                          {formatTime(record.checkOutTime, locale) || <span className="text-fg-muted">—</span>}
                        </TableCell>
                        <TableCell numeric>{record.workHours ? record.workHours.toFixed(1) : <span className="text-fg-muted">—</span>}</TableCell>
                        <TableCell className="!pl-8">
                          {/* 狀態色點＋異常文字橫排（原本三顆淺色膠囊直疊、時間後加警告 emoji） */}
                          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
                            <StatusDot tone={toneOf(attendanceTone, record.status)} label={t(`translation:attendanceStatus.${record.status || 'PRESENT'}`)} />
                            {flagLabels.length > 0 && <span className="text-[13px] text-red-600 dark:text-red-400">{flagLabels.join('・')}</span>}
                          </span>
                        </TableCell>
                        <TableCell className="truncate" title={record.notes || undefined}>{record.notes || <span className="text-fg-muted">—</span>}</TableCell>
                        <TableCell>
                          <RowActions>
                            <RowAction onClick={() => setAttendanceDialog(record)}>{t('translation:common.actions.edit')}</RowAction>
                            {admin && <RowAction tone="danger" onClick={() => attendanceStore.handleDelete(record.id)}>{t('translation:common.actions.delete')}</RowAction>}
                          </RowActions>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Table style={{ tableLayout: 'fixed', minWidth: '960px' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">{t('translation:common.table.columns.name')}</TableHead>
                    <TableHead className="w-28">{t('translation:common.table.columns.type')}</TableHead>
                    <TableHead numeric className="w-40">{t('pages:attendance.leave.period')}</TableHead>
                    <TableHead numeric className="w-28 whitespace-nowrap">{t('pages:attendance.leave.form.days')}</TableHead>
                    <TableHead className="w-28 !pl-8">{t('translation:common.table.columns.status')}</TableHead>
                    <TableHead>{t('translation:common.forms.labels.reason')}</TableHead>
                    <TableHead className="w-40">{t('translation:common.table.columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaveItems.length === 0 ? (
                    <ListEmptyRow
                      colSpan={7}
                      filtered={!!(search || leaveStatus)}
                      title={t('pages:attendance.list.noLeaves')}
                      hint={t('pages:attendance.leave.emptyHint')}
                      action={primaryButton}
                    />
                  ) : leaveItems.map(request => {
                    const pending = request.status === 'PENDING';
                    const sameDay = request.startDate.slice(0, 10) === request.endDate.slice(0, 10);
                    return (
                      <TableRow key={request.id} className={pendingRowClass(request)}>
                        <TableCell className="truncate font-medium text-fg">{request.employee?.name || '—'}</TableCell>
                        <TableCell className="truncate">{LEAVE_TYPES.includes(request.leaveType) ? t(`pages:attendance.leave.types.${request.leaveType}`) : request.leaveType}</TableCell>
                        <TableCell numeric>
                          {formatTableDate(request.startDate, i18n.language)}
                          {!sameDay && ` – ${formatTableDate(request.endDate, i18n.language)}`}
                        </TableCell>
                        <TableCell numeric>{request.days}</TableCell>
                        <TableCell className="!pl-8">
                          <StatusDot tone={toneOf(leaveTone, request.status)} label={t(`translation:leaveStatus.${request.status}`)} />
                        </TableCell>
                        <TableCell className="truncate" title={request.reason || undefined}>{request.reason || <span className="text-fg-muted">—</span>}</TableCell>
                        <TableCell>
                          {/* 待審核：核准／拒絕＋「⋯」（編輯、撤回）；已審核：只有管理員能刪除 */}
                          <RowActions>
                            {pending && admin && <RowAction onClick={() => review(request, 'APPROVED')}>{t('translation:common.actions.approve')}</RowAction>}
                            {pending && admin && <RowAction tone="danger" onClick={() => review(request, 'REJECTED')}>{t('translation:common.actions.reject')}</RowAction>}
                            {pending && !admin && <RowAction onClick={() => setLeaveDialog(request)}>{t('translation:common.actions.edit')}</RowAction>}
                            {pending && !admin && <RowAction tone="danger" onClick={() => leaveStore.handleDelete(request.id)}>{t('pages:attendance.leave.withdraw')}</RowAction>}
                            {pending && admin && (
                              <MoreMenu
                                variant="icon"
                                label={t('translation:common.table.columns.actions')}
                                items={[
                                  { key: 'edit', label: t('translation:common.actions.edit'), onClick: () => setLeaveDialog(request) },
                                  { key: 'delete', label: t('translation:common.actions.delete'), tone: 'danger', onClick: () => leaveStore.handleDelete(request.id) },
                                ]}
                              />
                            )}
                            {!pending && admin && <RowAction tone="danger" onClick={() => leaveStore.handleDelete(request.id)}>{t('translation:common.actions.delete')}</RowAction>}
                          </RowActions>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={active.store.showDeleteConfirmModal}
        onClose={active.store.closeDeleteModal}
        onConfirm={confirmDelete}
        title={tab === 'attendance' ? t('pages:attendance.list.confirmDeleteTitle') : t('pages:attendance.leave.confirmDeleteTitle')}
        message={tab === 'attendance' ? t('pages:attendance.list.confirmDeleteMessage') : t('pages:attendance.leave.confirmDeleteMessage')}
        confirmText={t('translation:common.actions.delete')}
        cancelText={t('translation:common.actions.cancel')}
        type="danger"
        isLoading={active.store.isDeleting}
        loadingText={t('translation:common.status.deleting')}
        t={t}
      />

      {checkInOpen && <QuickCheckInDialog onClose={() => setCheckInOpen(false)} />}
      {attendanceDialog !== undefined && <AttendanceDialog record={attendanceDialog ?? undefined} onClose={() => setAttendanceDialog(undefined)} />}
      {leaveDialog !== undefined && <LeaveRequestDialog leaveRequest={leaveDialog ?? undefined} onClose={() => setLeaveDialog(undefined)} />}
    </div>
  );
};

export default AttendanceListPage;
